import {
  type AccountListEntry,
  type AuthLoginResult,
  cloudSubscriptionLinkExisting,
  deviceGetId,
  familyDataDeleteLocal,
  guardianChallenge,
  trialMigrationExport,
} from '@chorepop/core/client';
import type { Session } from '@supabase/supabase-js';
import { openUrl } from '@tauri-apps/plugin-opener';
import { type Component, Show, createResource, createSignal } from 'solid-js';
import { CHOREPOP_FAMILY_MONTHLY } from '../billing/catalog';
import {
  acknowledgeChorePopFamily,
  loadSubscriptionStorefront,
  purchaseChorePopFamily,
  restoreChorePopFamily,
} from '../billing/storefront';
import {
  cancelFamilyDataDeletion,
  cancelTrialDataDeletion,
  completeVerifiedTrialSubscription,
  getCloudAuthProjectUrl,
  getCloudAuthPublishableKey,
  getConfiguredCloudEnvironment,
  requestFamilyDataDeletion,
  requestTrialDataDeletion,
} from '../cloud/cloudAuth';
import { ActionScreen } from '../components/ActionScreen';
import { FamilyDataDownloadPanel } from '../components/FamilyDataDownloadPanel';
import { useIdleSessionGuard } from '../lib/useIdleSessionGuard';

type SuccessSession = Extract<AuthLoginResult, { outcome: 'success' }>;

type Props = {
  account: AccountListEntry;
  session: SuccessSession;
  cloudSession: Session;
  cloudFamilyId: string | null;
  subscribeNow?: boolean;
  platform: 'android' | 'ios';
  onSubscribed: () => void;
  onSignOut: () => void;
  onDeletionRequested: (deleteAfter: string) => void;
};

export const SubscriptionRequiredScreen: Component<Props> = (props) => {
  useIdleSessionGuard({
    sessionId: props.session.sessionId,
    initialExpiresAtUtc: props.session.expiresAtUtc,
    onExpired: props.onSignOut,
    onForegroundLost: props.onSignOut,
  });
  const storeTarget = () => ({
    store: props.platform === 'ios' ? ('apple' as const) : ('google' as const),
    environment: getConfiguredCloudEnvironment(),
  });
  const [products] = createResource(() => loadSubscriptionStorefront(storeTarget()));
  const [busy, setBusy] = createSignal<'purchase' | 'restore' | 'delete' | null>(null);
  const [message, setMessage] = createSignal<string | null>(null);
  const [credential, setCredential] = createSignal('');
  const [confirmation, setConfirmation] = createSignal('');
  const phrase = () => `DELETE ${props.account.displayName}`;
  const openExternalLink = (event: MouseEvent, url: string) => {
    event.preventDefault();
    void openUrl(url);
  };

  const migrate = async (mode: 'purchase' | 'restore') => {
    setBusy(mode);
    setMessage(null);
    try {
      const exported = await trialMigrationExport({ sessionId: props.session.sessionId });
      if (!exported.ok) throw new Error(exported.error.message);
      const device = await deviceGetId();
      if (!device.ok) throw new Error(device.error.message);
      const purchase =
        mode === 'purchase'
          ? await purchaseChorePopFamily(props.cloudSession.user.id, storeTarget())
          : await restoreChorePopFamily(storeTarget());
      const migrated = await completeVerifiedTrialSubscription({
        store: props.platform === 'ios' ? 'apple' : 'google',
        purchase,
        deviceId: device.value,
        platform: props.platform,
        migration: exported.value,
      });
      const linked = await cloudSubscriptionLinkExisting({
        sessionId: props.session.sessionId,
        localAccountId: props.account.accountId,
        accessToken: props.cloudSession.access_token,
        publishableKey: getCloudAuthPublishableKey(),
        supabaseUrl: getCloudAuthProjectUrl(),
        familyId: migrated.familyId,
        familyAccountId: migrated.guardianAccountId,
      });
      if (!linked.ok) throw new Error(linked.error.message);
      if (props.platform === 'android') await acknowledgeChorePopFamily(purchase.purchaseToken);
      props.onSubscribed();
    } catch (caught) {
      setMessage(
        caught instanceof Error ? caught.message : 'The subscription could not be completed.',
      );
    } finally {
      setBusy(null);
    }
  };

  const deleteTrial = async (event: Event) => {
    event.preventDefault();
    setMessage(null);
    if (confirmation().trim() !== phrase()) {
      setMessage(`Type ${phrase()} exactly to continue.`);
      return;
    }
    if (!credential()) {
      setMessage('Enter your guardian password or 6-digit PIN.');
      return;
    }
    setBusy('delete');
    try {
      const challenge = await guardianChallenge({
        accountId: props.account.accountId,
        credential: /^\d{6}$/.test(credential())
          ? { kind: 'pin', pin: credential() }
          : { kind: 'password', password: credential() },
      });
      if (!challenge.ok || challenge.value.outcome !== 'success') {
        throw new Error('That guardian credential was not accepted.');
      }
      const deleteAfter = props.cloudFamilyId
        ? (await requestFamilyDataDeletion(props.cloudFamilyId)).deleteAfter
        : await requestTrialDataDeletion();
      const local = await familyDataDeleteLocal({ sessionId: props.session.sessionId });
      if (!local.ok) {
        if (props.cloudFamilyId) {
          await cancelFamilyDataDeletion(props.cloudFamilyId);
        } else {
          await cancelTrialDataDeletion();
        }
        throw new Error(local.error.message);
      }
      props.onDeletionRequested(deleteAfter);
    } catch (caught) {
      setMessage(
        caught instanceof Error ? caught.message : 'Could not schedule trial data deletion.',
      );
    } finally {
      setCredential('');
      setBusy(null);
    }
  };

  return (
    <ActionScreen
      eyebrow={props.cloudFamilyId || props.subscribeNow ? 'ChorePop Family' : '30-day trial'}
      title={
        props.subscribeNow
          ? 'Start your subscription'
          : props.cloudFamilyId
            ? 'Your subscription needs attention'
            : 'Your free trial has ended'
      }
      onBack={props.onSignOut}
      class="action-screen--settings subscription-required-screen"
    >
      <div class="settings-card">
        <section class="settings-section subscription-required-summary">
          <h2>
            {props.subscribeNow
              ? 'Subscribe now'
              : props.account.roleCode === 'guardian'
                ? 'Keep your family moving'
                : 'Ask a guardian to subscribe'}
          </h2>
          <p class="settings-section-intro">
            {props.subscribeNow ? (
              <>
                Your family is ready. Subscribe now to turn on secure cloud sync and multi-device
                access without using the free trial.
              </>
            ) : (
              <>
                Your family data is still safe on this device, but it is read-only until a guardian{' '}
                {props.cloudFamilyId ? 'renews or restores the subscription' : 'subscribes'}.
              </>
            )}
          </p>
          <Show when={props.account.roleCode === 'guardian'}>
            <p class="subscription-price" data-testid="subscription-price">
              {products()?.[0]?.formattedPrice ?? CHOREPOP_FAMILY_MONTHLY.fallbackPrice} · cancel
              through {props.platform === 'ios' ? 'Apple' : 'Google Play'}
            </p>
            <p class="subscription-legal-links" data-testid="subscription-legal-links">
              <Show when={props.platform === 'ios'}>
                By subscribing, you agree to the{' '}
                <a
                  href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/"
                  target="_blank"
                  rel="noreferrer"
                  onClick={(event) =>
                    openExternalLink(
                      event,
                      'https://www.apple.com/legal/internet-services/itunes/dev/stdeula/',
                    )
                  }
                >
                  Terms of Use
                </a>{' '}
                and{' '}
              </Show>
              <Show when={props.platform === 'android'}>Please review our </Show>
              <a
                href="https://chorepop.com/privacy"
                target="_blank"
                rel="noreferrer"
                onClick={(event) => openExternalLink(event, 'https://chorepop.com/privacy')}
              >
                Privacy Policy
              </a>
              .
            </p>
            <Show when={products.error}>
              <p class="form-error" role="alert">
                {products.error instanceof Error
                  ? products.error.message
                  : 'The store is unavailable.'}
              </p>
            </Show>
            <div class="settings-section-actions">
              <button
                type="button"
                class="primary"
                disabled={busy() !== null || products.loading || !products()?.length}
                onClick={() => void migrate('purchase')}
                data-testid="subscription-purchase"
              >
                {busy() === 'purchase' ? 'Checking purchase…' : 'Subscribe for £2.99 a month'}
              </button>
              <button
                type="button"
                class="secondary"
                disabled={busy() !== null}
                onClick={() => void migrate('restore')}
                data-testid="subscription-restore"
              >
                {busy() === 'restore' ? 'Checking purchases…' : 'Restore purchase'}
              </button>
            </div>
          </Show>
          <Show when={message()}>
            {(text) => (
              <p class="form-error" role="alert">
                {text()}
              </p>
            )}
          </Show>
        </section>

        <Show when={props.account.roleCode === 'guardian'}>
          <FamilyDataDownloadPanel sessionId={props.session.sessionId} />
          <section class="settings-section settings-privacy-section">
            <h2>{props.cloudFamilyId ? 'Delete my family data' : 'Delete my trial data'}</h2>
            <p class="settings-section-intro">
              This removes the local family ledger and schedules{' '}
              {props.cloudFamilyId ? 'the whole cloud family' : 'the trial identity'} for deletion
              after 30 days.
            </p>
            <form class="privacy-delete-form" onSubmit={deleteTrial}>
              <label class="field">
                <span class="field-label">
                  Type <strong>{phrase()}</strong>
                </span>
                <input
                  value={confirmation()}
                  onInput={(event) => setConfirmation(event.currentTarget.value)}
                  disabled={busy() !== null}
                  data-testid="trial-delete-confirmation"
                />
              </label>
              <label class="field">
                <span class="field-label">Guardian password or PIN</span>
                <input
                  type="password"
                  autocomplete="current-password"
                  value={credential()}
                  onInput={(event) => setCredential(event.currentTarget.value)}
                  disabled={busy() !== null}
                  data-testid="trial-delete-credential"
                />
              </label>
              <button
                type="submit"
                class="danger"
                disabled={
                  busy() !== null || confirmation().trim() !== phrase() || credential().length === 0
                }
                data-testid="trial-delete-submit"
              >
                {busy() === 'delete' ? 'Deleting…' : 'Delete my trial data'}
              </button>
            </form>
          </section>
        </Show>
      </div>
    </ActionScreen>
  );
};
